import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Typography,
  Button,
  Spin,
  Result,
  Space,
  Divider,
  Tag,
  Modal,
  Input,
  Form,
  message,
  Alert,
} from 'antd';
import {
  CalendarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  UserOutlined,
  BankOutlined,
  ArrowLeftOutlined,
  QrcodeOutlined,
  ShareAltOutlined,
  StarFilled,
  KeyOutlined,
  MailOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  PhoneOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import dayjs from 'dayjs';
import PagesWrapper from '../../../PagesWrapper';
import * as eventsService from '../../../services/eventsService';
import { getFileUrl } from '../../../utils/urlUtils';
import styles from './EventDetail.module.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const EventDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);
  const [qrModalVisible, setQrModalVisible] = useState(false);

  // Registration check state
  const [checkEmail, setCheckEmail] = useState('');
  const [checkCode, setCheckCode] = useState('');
  const [checkLoading, setCheckLoading] = useState(false);
  const [myRegistration, setMyRegistration] = useState(null);
  const [registrationError, setRegistrationError] = useState(null);
  
  // Edit modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [editSaving, setEditSaving] = useState(false);
  
  // Cancel modal state
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [id]);

  const fetchEvent = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await eventsService.getPublicEvent(id);
      setEvent(data);
    } catch (error) {
      if (error.status === 404) {
        setError('notFound');
      } else {
        setError('generic');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckRegistration = async () => {
    if (!checkEmail || !checkCode) {
      setRegistrationError(t('events.registration.fillBoth', 'Please enter both email and edit code'));
      return;
    }

    setCheckLoading(true);
    setRegistrationError(null);
    try {
      const registration = await eventsService.getMyRegistration(id, checkEmail, checkCode);
      setMyRegistration(registration);
    } catch (error) {
      if (error.status === 404) {
        setRegistrationError(t('events.registration.invalidCode', 'Invalid edit code or email'));
      } else {
        setRegistrationError(t('events.registration.checkError', 'Failed to verify registration'));
      }
    } finally {
      setCheckLoading(false);
    }
  };

  const handleClearRegistration = () => {
    setMyRegistration(null);
    setCheckEmail('');
    setCheckCode('');
    setRegistrationError(null);
  };

  const handleOpenEditModal = () => {
    editForm.setFieldsValue({
      firstName: myRegistration.firstName,
      lastName: myRegistration.lastName,
      phone: myRegistration.phone || '',
      remarks: myRegistration.remarks || '',
    });
    setEditModalVisible(true);
  };

  const handleSaveEdit = async (values) => {
    setEditSaving(true);
    try {
      const result = await eventsService.updateMyRegistration(id, {
        email: checkEmail,
        editCode: checkCode,
        ...values,
      });
      setMyRegistration(result);
      setEditModalVisible(false);
      message.success(t('events.registration.updateSuccess', 'Registration updated successfully'));
    } catch (error) {
      message.error(t('events.registration.updateError', 'Failed to update registration'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleCancelRegistration = async () => {
    setCancelling(true);
    try {
      await eventsService.cancelMyRegistration(id, checkEmail, checkCode);
      setCancelModalVisible(false);
      handleClearRegistration();
      message.success(t('events.registration.cancelSuccess', 'Registration cancelled successfully'));
      // Refresh event to update registration count
      fetchEvent();
    } catch (error) {
      message.error(t('events.registration.cancelError', 'Failed to cancel registration'));
    } finally {
      setCancelling(false);
    }
  };

  const formatEventDate = (startDate, endDate) => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    
    if (start.isSame(end, 'day')) {
      return (
        <>
          <div className={styles.dateDay}>{start.format('dddd, MMMM D, YYYY')}</div>
          <div className={styles.dateTime}>{start.format('HH:mm')} - {end.format('HH:mm')}</div>
        </>
      );
    }
    return (
      <>
        <div>{start.format('dddd, MMMM D, YYYY [at] HH:mm')}</div>
        <div>{t('events.to', 'to')}</div>
        <div>{end.format('dddd, MMMM D, YYYY [at] HH:mm')}</div>
      </>
    );
  };

  const handleShare = async () => {
    const url = window.location.href;
    const shareData = {
      title: event.title,
      text: event.purpose || t('events.shareText', 'Check out this event!'),
      url: url,
    };

    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(url);
        }
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success(t('events.linkCopied', 'Link copied to clipboard'));
  };

  const handleDownloadQrCode = () => {
    const svg = document.getElementById('event-detail-qr-code');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `${event?.title || 'event'}-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const isPastEvent = event && dayjs(event.endDateTime).isBefore(dayjs());

  if (loading) {
    return (
      <PagesWrapper>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      </PagesWrapper>
    );
  }

  if (error) {
    return (
      <PagesWrapper>
        <Result
          status={error === 'notFound' ? '404' : 'error'}
          title={error === 'notFound' 
            ? t('events.notFound', 'Event Not Found')
            : t('events.loadError', 'Failed to Load Event')
          }
          subTitle={error === 'notFound'
            ? t('events.notFoundDesc', 'This event may have been removed or is no longer available.')
            : t('events.loadErrorDesc', 'Something went wrong. Please try again later.')
          }
          extra={[
            <Button type="primary" key="back" onClick={() => navigate('/events')}>
              {t('events.backToEvents', 'Back to Events')}
            </Button>,
          ]}
        />
      </PagesWrapper>
    );
  }

  return (
    <PagesWrapper>
      <div className={styles.container}>
        {/* Back Link */}
        <Link to="/events" className={styles.backLink}>
          <ArrowLeftOutlined /> {t('events.backToEvents', 'Back to Events')}
        </Link>

        {/* Banner */}
        {event.bannerImageUrl && (
          <div className={styles.bannerContainer}>
            <img 
              src={getFileUrl(event.bannerImageUrl)} 
              alt={event.title}
              className={styles.bannerImage}
            />
            {event.isFeatured && (
              <Tag color="gold" className={styles.featuredTag}>
                <StarFilled /> {t('events.featured', 'Featured')}
              </Tag>
            )}
          </div>
        )}

        <div className={styles.contentGrid}>
          {/* Main Content */}
          <div className={styles.mainContent}>
            <Title level={2} className={styles.eventTitle}>
              {event.title}
            </Title>

            {event.purpose && (
              <Paragraph className={styles.purpose}>
                {event.purpose}
              </Paragraph>
            )}

            {event.richContent && (
              <>
                <Divider>{t('events.details', 'Event Details')}</Divider>
                <div 
                  className={styles.richContent}
                  dangerouslySetInnerHTML={{ __html: event.richContent }}
                />
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className={styles.sidebar}>
            {/* Registration Card */}
            <Card className={styles.registrationCard}>
              {isPastEvent ? (
                <div className={styles.pastEventNotice}>
                  <Text type="secondary">
                    {t('events.pastEvent', 'This event has ended')}
                  </Text>
                </div>
              ) : (
                <Button 
                  type="primary" 
                  size="large" 
                  block
                  onClick={() => navigate(`/events/${id}/register`)}
                >
                  {t('events.registerNow', 'Register Now')}
                </Button>
              )}

              {event.registrationCount > 0 && (
                <div className={styles.registrationCount}>
                  <TeamOutlined />
                  <Text>
                    {t('events.registeredCount', '{{count}} people registered', { count: event.registrationCount })}
                  </Text>
                </div>
              )}
            </Card>

            {/* Already Registered Card */}
            <Card className={styles.alreadyRegisteredCard} size="small">
              <Text strong className={styles.alreadyRegisteredTitle}>
                <KeyOutlined /> {t('events.alreadyRegistered', 'Already registered?')}
              </Text>
              
              {myRegistration ? (
                <div className={styles.myRegistrationDetails}>
                  <Alert
                    type="success"
                    message={t('events.registration.verified', 'Registration verified')}
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  
                  <div className={styles.registrationDetail}>
                    <Text type="secondary">{t('events.registration.name', 'Name')}:</Text>
                    <Text strong>{myRegistration.firstName} {myRegistration.lastName}</Text>
                  </div>
                  
                  <div className={styles.registrationDetail}>
                    <Text type="secondary">{t('events.registration.email', 'Email')}:</Text>
                    <Text>{myRegistration.email}</Text>
                  </div>

                  {myRegistration.meetingUrl && (
                    <div className={styles.registrationDetailVertical}>
                      <Text type="secondary">{t('events.registration.remoteAttendance', 'Remote Attendance')}:</Text>
                      <a
                        href={myRegistration.meetingUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.meetingLink}
                      >
                        <LinkOutlined /> {t('events.registration.openMeetingLink', 'Open meeting link')}
                      </a>
                    </div>
                  )}
                  
                  {myRegistration.phone && (
                    <div className={styles.registrationDetail}>
                      <Text type="secondary">{t('events.registration.phone', 'Phone')}:</Text>
                      <Text>{myRegistration.phone}</Text>
                    </div>
                  )}
                  
                  {myRegistration.remarks && (
                    <div className={styles.registrationDetailVertical}>
                      <Text type="secondary">{t('events.registration.remarks', 'Remarks')}:</Text>
                      <Text>{myRegistration.remarks}</Text>
                    </div>
                  )}
                  
                  <div className={styles.registrationDetail}>
                    <Text type="secondary">{t('events.registration.registeredAt', 'Registered')}:</Text>
                    <Text>{dayjs(myRegistration.registeredAt).format('DD/MM/YYYY HH:mm')}</Text>
                  </div>
                  
                  {myRegistration.modifiedAt && (
                    <div className={styles.registrationDetail}>
                      <Text type="secondary">{t('events.registration.modifiedAt', 'Last modified')}:</Text>
                      <Text>{dayjs(myRegistration.modifiedAt).format('DD/MM/YYYY HH:mm')}</Text>
                    </div>
                  )}
                  
                  <Divider style={{ margin: '12px 0' }} />
                  
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {!isPastEvent && (
                      <Button 
                        icon={<EditOutlined />} 
                        block 
                        onClick={handleOpenEditModal}
                      >
                        {t('events.registration.editDetails', 'Edit Details')}
                      </Button>
                    )}
                    <Button 
                      danger 
                      icon={<DeleteOutlined />} 
                      block
                      onClick={() => setCancelModalVisible(true)}
                    >
                      {t('events.registration.cancel', 'Cancel Registration')}
                    </Button>
                    <Button type="link" block onClick={handleClearRegistration}>
                      {t('events.registration.checkAnother', 'Check another registration')}
                    </Button>
                  </Space>
                </div>
              ) : (
                <div className={styles.checkRegistrationForm}>
                  <Paragraph type="secondary" style={{ fontSize: 13, margin: '8px 0' }}>
                    {t('events.registration.enterCodeDesc', 'Enter your email and edit code to view or edit your registration.')}
                  </Paragraph>
                  
                  <Input
                    prefix={<MailOutlined />}
                    placeholder={t('events.registration.emailPlaceholder', 'your.email@example.com')}
                    value={checkEmail}
                    onChange={(e) => setCheckEmail(e.target.value)}
                    style={{ marginBottom: 8 }}
                  />
                  
                  <Input
                    prefix={<KeyOutlined />}
                    placeholder={t('events.registration.codePlaceholder', 'Edit code (8 characters)')}
                    value={checkCode}
                    onChange={(e) => setCheckCode(e.target.value.toUpperCase())}
                    maxLength={8}
                    style={{ marginBottom: 8 }}
                    onPressEnter={handleCheckRegistration}
                  />
                  
                  {registrationError && (
                    <Alert 
                      type="error" 
                      message={registrationError} 
                      showIcon 
                      style={{ marginBottom: 8 }}
                    />
                  )}
                  
                  <Button 
                    type="primary" 
                    icon={<CheckOutlined />}
                    block 
                    onClick={handleCheckRegistration}
                    loading={checkLoading}
                  >
                    {t('events.registration.viewRegistration', 'View Registration')}
                  </Button>
                </div>
              )}
            </Card>

            {/* Event Info Card */}
            <Card className={styles.infoCard}>
              <div className={styles.infoItem}>
                <CalendarOutlined className={styles.infoIcon} />
                <div className={styles.infoContent}>
                  <Text strong>{t('events.dateTime', 'Date & Time')}</Text>
                  <div className={styles.infoValue}>
                    {formatEventDate(event.startDateTime, event.endDateTime)}
                  </div>
                </div>
              </div>

              <Divider className={styles.infoDivider} />

              <div className={styles.infoItem}>
                <EnvironmentOutlined className={styles.infoIcon} />
                <div className={styles.infoContent}>
                  <Text strong>{t('events.location', 'Location')}</Text>
                  <div className={styles.infoValue}>{event.location}</div>
                </div>
              </div>

              {event.organization && (
                <>
                  <Divider className={styles.infoDivider} />
                  <div className={styles.infoItem}>
                    <BankOutlined className={styles.infoIcon} />
                    <div className={styles.infoContent}>
                      <Text strong>{t('events.organization', 'Organization')}</Text>
                      <div className={styles.infoValue}>{event.organization}</div>
                    </div>
                  </div>
                </>
              )}

              {event.organizer && (
                <>
                  <Divider className={styles.infoDivider} />
                  <div className={styles.infoItem}>
                    <UserOutlined className={styles.infoIcon} />
                    <div className={styles.infoContent}>
                      <Text strong>{t('events.organizer', 'Organizer')}</Text>
                      <div className={styles.infoValue}>{event.organizer}</div>
                    </div>
                  </div>
                </>
              )}
            </Card>

            {/* Share Card */}
            <Card className={styles.shareCard}>
              <Text strong className={styles.shareTitle}>
                {t('events.shareEvent', 'Share this event')}
              </Text>
              <Space className={styles.shareButtons}>
                <Button 
                  icon={<ShareAltOutlined />}
                  onClick={handleShare}
                >
                  {t('events.share', 'Share')}
                </Button>
                <Button 
                  icon={<QrcodeOutlined />}
                  onClick={() => setQrModalVisible(true)}
                >
                  {t('events.qrCode', 'QR Code')}
                </Button>
              </Space>
            </Card>
          </div>
        </div>

        {/* QR Code Modal */}
        <Modal
          title={t('events.qrCodeTitle', 'Event QR Code')}
          open={qrModalVisible}
          onCancel={() => setQrModalVisible(false)}
          footer={[
            <Button key="download" type="primary" onClick={handleDownloadQrCode}>
              {t('events.downloadQr', 'Download QR Code')}
            </Button>,
            <Button key="close" onClick={() => setQrModalVisible(false)}>
              {t('common.close', 'Close')}
            </Button>,
          ]}
          centered
        >
          <div className={styles.qrContainer}>
            <QRCodeSVG
              id="event-detail-qr-code"
              value={window.location.href}
              size={256}
              level="H"
              includeMargin
            />
            <Text className={styles.qrEventName}>{event.title}</Text>
          </div>
        </Modal>

        {/* Edit Registration Modal */}
        <Modal
          title={t('events.registration.editTitle', 'Edit Registration')}
          open={editModalVisible}
          onCancel={() => setEditModalVisible(false)}
          footer={null}
          destroyOnClose
        >
          <Alert
            type="info"
            message={t('events.registration.emailCannotChange', 'Email address cannot be changed')}
            style={{ marginBottom: 16 }}
            showIcon
          />
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleSaveEdit}
          >
            <Form.Item
              name="firstName"
              label={t('events.registration.firstName', 'First Name')}
              rules={[{ required: true, message: t('events.registration.firstNameRequired', 'First name is required') }]}
            >
              <Input prefix={<UserOutlined />} />
            </Form.Item>

            <Form.Item
              name="lastName"
              label={t('events.registration.lastName', 'Last Name')}
              rules={[{ required: true, message: t('events.registration.lastNameRequired', 'Last name is required') }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="phone"
              label={t('events.registration.phone', 'Phone Number')}
            >
              <Input prefix={<PhoneOutlined />} />
            </Form.Item>

            <Form.Item
              name="remarks"
              label={t('events.registration.remarks', 'Remarks')}
            >
              <TextArea rows={3} maxLength={500} showCount />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setEditModalVisible(false)}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button type="primary" htmlType="submit" loading={editSaving}>
                  {t('common.save', 'Save')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Cancel Registration Modal */}
        <Modal
          title={t('events.registration.cancelTitle', 'Cancel Registration')}
          open={cancelModalVisible}
          onCancel={() => setCancelModalVisible(false)}
          onOk={handleCancelRegistration}
          okText={t('events.registration.confirmCancel', 'Yes, Cancel Registration')}
          okButtonProps={{ danger: true, loading: cancelling }}
          cancelText={t('common.no', 'No')}
        >
          <Paragraph>
            {t('events.registration.cancelConfirm', 'Are you sure you want to cancel your registration for this event?')}
          </Paragraph>
          <Paragraph type="secondary">
            {t('events.registration.cancelWarning', 'This action cannot be undone. You will need to register again if you change your mind.')}
          </Paragraph>
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default EventDetail;
